import React, { useEffect, useId, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import InputTooltip, { placement } from "@/components/general/tooltip";
import GenericDateRange from "@/components/dateTime/genericDateRange";
import styles from "@/components/forms/inputsWithRef/dateFieldWithRef.module.css";

interface Props {
  mandatory?: boolean;
  label?: string;
  readonly?: boolean;
  name?: string;
  required?: boolean;
  onChange?: (event: any) => void;
  onBlur?: (event?: any) => void;
  value?: any;
  defaultValue?: any;
  tooltip?: boolean;
  placement?: placement;
  tooltipMessage?: any;
}

const toPickerValue = (value: any): Dayjs | null => {
  if (!value) return null;
  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue : null;
};

export const DateFieldWithRef = React.forwardRef<HTMLInputElement, Props>(
  function DateFieldWithRef(props, ref) {
    const generatedId = useId();
    const inputId = props.name ?? generatedId;
    const [internalValue, setInternalValue] = useState<Dayjs | null>(() =>
      toPickerValue(props.defaultValue),
    );
    const value =
      props.value !== undefined
        ? toPickerValue(props.value)
        : internalValue;

    useEffect(() => {
      if (props.value === undefined) {
        setInternalValue(toPickerValue(props.defaultValue));
      }
    }, [props.defaultValue, props.value]);

    const handleChange = (newValue: Dayjs | null) => {
      setInternalValue(newValue);
      props.onChange?.({
        target: {
          id: inputId,
          name: props.name,
          value: newValue ? newValue.format("YYYY-MM-DD") : null,
        },
      });
    };

    return (
      <div>
        <label className={styles.label} htmlFor={inputId}>
          {props.label}
          {props.required === true && (
            <span style={{ color: "red" }}>&nbsp;{"\u{002A}"}</span>
          )}
          {props.tooltip && (
            <InputTooltip
              tooltipMessage={props.tooltipMessage}
              placement={props.placement}
            />
          )}
        </label>
        <GenericDateRange
          selectionMode="single"
          value={value}
          onChange={handleChange}
          disabled={props.readonly}
          textFieldProps={{
            id: inputId,
            name: props.name,
            required: props.required,
            onBlur: props.onBlur,
            inputRef: ref,
          }}
        />
      </div>
    );
  }
);
